import "./InformationBox.css";

import { ReactNode } from "react";

import { AlertCoralIcon, AlertIcon, InformationIcon, RocketLargeCoralIcon, SuccessIcon } from "../../../assets";
import { logError } from "../../../service/error";
import { capitalize } from "../../../utils";
import { LoadingSpinner } from "../../general";

type InformationBoxVariant = "success" | "secondarySuccess" | "alert" | "warning" | "info" | "loading" | "pilotProject";
type InformationBoxDirection = "column" | "row";
type InformationBoxCorners = "extraRounded" | "rounded" | "default";
type InformationBoxSize = "default" | "small";

export interface InformationBoxProps {
  variant: InformationBoxVariant;
  direction?: InformationBoxDirection;
  corners?: InformationBoxCorners;
  size?: InformationBoxSize;
  title?: string;
  children?: ReactNode;
  onEdit?: () => void;
  editText?: string;
  editIcon?: JSX.Element;
  editDirection?: "default" | "reverse";
  hasBorder?: boolean;
  hasUnderline?: boolean;
}

const getIcon = (variant: InformationBoxVariant): JSX.Element | null => {
  switch (variant) {
    case "success":
    case "secondarySuccess":
      return <SuccessIcon />;
    case "alert":
      return <AlertIcon />;
    case "warning":
      return <AlertCoralIcon />;
    case "info":
      return <InformationIcon />;
    case "loading":
      return <LoadingSpinner type="dark" />;
    case "pilotProject":
      return <RocketLargeCoralIcon />;
    default:
      logError({ error: `No valid InformationBox could be found for variant: ${variant}` });
      return null;
  }
};

export const InformationBox = ({
  variant,
  direction = "column",
  corners = "default",
  size = "default",
  title,
  children,
  onEdit,
  editText = "Edit",
  editIcon,
  editDirection = "default",
  hasBorder = false,
  hasUnderline = true,
}: InformationBoxProps): JSX.Element => {
  return (
    <div
      className={`InformationBox InformationBox${capitalize(variant)} InformationBox${capitalize(
        direction
      )} InformationBox${capitalize(corners)}Corners InformationBox${capitalize(size)}Size InformationBox${hasBorder ? "WithBorder" : "WithoutBorder"}`}
    >
      {title ? (
        <>
          <div className={`InformationBoxHeaderArea ${children ? "" : "InformationBoxNoChildren"}`}>
            <div className="InformationBoxIconContainer">{getIcon(variant)}</div>
            <h3>{title}</h3>
          </div>
          {children && <div className="body2 InformationBoxTextArea">{children}</div>}
        </>
      ) : (
        <div className="InformationBoxWithoutHeaderArea">
          <div className="InformationBoxIconContainer">{getIcon(variant)}</div>
          {children}
        </div>
      )}
      {onEdit !== undefined && (
        <div
          className={`EditButton ${hasUnderline ? "EditButton_Underline" : ""} ${editDirection === "reverse" ? "EditButton_Reverse" : ""}`}
          onClick={onEdit}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              onEdit();
            }
          }}
          tabIndex={0}
          role="button"
        >
          <p className="body1">{editText}</p>
          {editIcon}
        </div>
      )}
    </div>
  );
};
